import React, { useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Float, MeshDistortMaterial } from '@react-three/drei';

function ShipModel({ shipLevel = 1, type = 'scout' }) {
  const ref = useRef();
  const scale = 0.5 + shipLevel * 0.08;

  // Color based on type
  const color = type === 'scout' ? '#3b82f6' : type === 'hauler' ? '#22c55e' : '#a855f7';
  const emissive = type === 'scout' ? '#1d4ed8' : type === 'hauler' ? '#15803d' : '#7c3aed';

  useFrame((_, delta) => {
    if (ref.current) {
      ref.current.rotation.y += delta * 0.4;
    }
  });

  return (
    <Float speed={2} rotationIntensity={0.3} floatIntensity={0.5}>
      <group ref={ref} scale={scale}>
        {/* Hull */}
        <mesh>
          <coneGeometry args={[0.4, 1.6, 4]} />
          <MeshDistortMaterial
            color={color}
            emissive={emissive}
            emissiveIntensity={0.4}
            metalness={0.9}
            roughness={0.2}
            distort={0.05}
            speed={2}
          />
        </mesh>
        {/* Wings */}
        <mesh position={[0.6, -0.2, 0]} rotation={[0, 0, -0.3]}>
          <boxGeometry args={[0.8, 0.05, 0.3]} />
          <meshStandardMaterial color={color} metalness={0.9} roughness={0.3} />
        </mesh>
        <mesh position={[-0.6, -0.2, 0]} rotation={[0, 0, 0.3]}>
          <boxGeometry args={[0.8, 0.05, 0.3]} />
          <meshStandardMaterial color={color} metalness={0.9} roughness={0.3} />
        </mesh>
        {/* Engine glow */}
        <mesh position={[0, -0.9, 0]}>
          <sphereGeometry args={[0.2, 16, 16]} />
          <meshBasicMaterial color="#60a5fa" transparent opacity={0.7} />
        </mesh>
        {/* Level indicator rings */}
        {Array.from({ length: Math.min(shipLevel, 5) }).map((_, i) => (
          <mesh key={i} position={[0, -0.3 - i * 0.15, 0]} rotation={[Math.PI / 2, 0, 0]}>
            <torusGeometry args={[0.5 + i * 0.05, 0.01, 8, 32]} />
            <meshBasicMaterial color="#60a5fa" transparent opacity={0.4 - i * 0.06} />
          </mesh>
        ))}
      </group>
    </Float>
  );
}

function DroneModel({ droneLevel = 1, type = 'mining' }) {
  const ref = useRef();

  const color = type === 'mining' ? '#fbbf24' : type === 'combat' ? '#ef4444' : '#22d3ee';

  useFrame((_, delta) => {
    if (ref.current) {
      ref.current.rotation.y += delta * 0.8;
    }
  });

  const scale = 0.4 + droneLevel * 0.05;

  return (
    <Float speed={3} rotationIntensity={0.2} floatIntensity={0.8}>
      <group ref={ref} scale={scale}>
        {/* Core */}
        <mesh>
          <octahedronGeometry args={[0.4, 0]} />
          <MeshDistortMaterial
            color={color}
            emissive={color}
            emissiveIntensity={0.3}
            metalness={0.8}
            roughness={0.3}
            distort={0.1}
            speed={3}
          />
        </mesh>
        {/* Arms */}
        {[0, Math.PI / 2, Math.PI, Math.PI * 1.5].map((angle, i) => (
          <mesh key={i} position={[Math.cos(angle) * 0.5, 0, Math.sin(angle) * 0.5]} rotation={[0, angle, 0]}>
            <boxGeometry args={[0.4, 0.05, 0.05]} />
            <meshStandardMaterial color={color} metalness={0.8} roughness={0.3} />
          </mesh>
        ))}
        {/* Hover ring */}
        <mesh rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.6, 0.02, 8, 32]} />
          <meshBasicMaterial color={color} transparent opacity={0.5} />
        </mesh>
      </group>
    </Float>
  );
}

export function ShipViewer({ ship }) {
  return (
    <div className="model-viewer">
      <Canvas camera={{ position: [0, 0, 3], fov: 50 }} gl={{ antialias: true, alpha: true }}>
        <ambientLight intensity={0.3} />
        <pointLight position={[3, 3, 3]} intensity={1} color="#6fa8ff" />
        <pointLight position={[-3, -1, 2]} intensity={0.5} color="#a855f7" />
        <ShipModel shipLevel={ship.level} type={ship.type} />
      </Canvas>
    </div>
  );
}

export function DroneViewer({ drone }) {
  return (
    <div className="model-viewer model-viewer-sm">
      <Canvas camera={{ position: [0, 0, 2.5], fov: 50 }} gl={{ antialias: true, alpha: true }}>
        <ambientLight intensity={0.3} />
        <pointLight position={[2, 2, 2]} intensity={0.8} color="#fbbf24" />
        <pointLight position={[-2, -1, 1]} intensity={0.4} color="#60a5fa" />
        <DroneModel droneLevel={drone.level} type={drone.type} />
      </Canvas>
    </div>
  );
}
